// ============================================================================
// Lexique de la feuille de brouillon (pur, sans DOM).
//   - lex()          : texte → atomes (« BC² » → ["BC", "²"], « 2ⁿ⁺¹ » → ["2ⁿ⁺¹"])
//   - normalize()    : multiplication implicite (« 3x » → 3 × x)
//   - checkSyntax()  : ligne partielle ou complète bien formée ?
//   - formVariants() : écritures équivalentes (commutativité, sens de =)
// Porté du prototype `engine.js` sans changement de comportement.
// ============================================================================

const SEP = '\u0001';
const BIN: Record<string, 1> = { '+': 1, '−': 1, '×': 1, '÷': 1, '^': 1, parmi: 1 };

/**
 * « k parmi n » (moteur v27) : le coefficient binomial, écrit comme il se lit. Un MOT
 * de la ligne, jamais une grandeur : « 2 parmi 5 » vaut 10, « k parmi n » vaut
 * n! ÷ (k! × (n − k)!). Il lie plus fort que × et ÷ : « 2 × 3 parmi 5 » = 2 × (3 parmi 5).
 */
export const PARMI = 'parmi';
export function isParmi(a: string | undefined): boolean {
  return a === PARMI;
}

/**
 * Fonctions usuelles (moteur v2) : un bout « ln » s'applique à ce qui suit,
 * comme « √ » (« ln(5) », « 2cos(x) »). Ce ne sont PAS des grandeurs.
 */
export const FUNCS: Record<string, 1> = {
  ln: 1,
  exp: 1,
  cos: 1,
  sin: 1,
  tan: 1,
  arg: 1,
  // moteur v17 : fonctions réciproques, en degrés (collège : B = arccos(0,8) ≈ 37°)
  arccos: 1,
  arcsin: 1,
  arctan: 1,
  // moteur v45 : logarithme décimal (log(1000) = 3)
  log: 1,
};
/** Constantes (moteur v2) : π vaut π, jamais une grandeur à trouver. */
export const CONSTS: Record<string, number> = { π: Math.PI };
/** Comparaisons (moteur v5) : « Δ > 0 », « 0 ≤ p ». Une seule par ligne. */
export const CMP: Record<string, 1> = { '>': 1, '<': 1, '≥': 1, '≤': 1, '≈': 1 };
export function isCmp(a: string | undefined): boolean {
  return a !== undefined && CMP[a] === 1;
}
/** sens d'une comparaison dans une chaîne (moteur v23) : « < » et « ≤ » vers le haut */
const LOWER_CMP: Record<string, boolean> = { '<': true, '≤': true, '>': false, '≥': false };

export function isFunc(a: string | undefined): boolean {
  return !!a && FUNCS[a] === 1;
}
export function isConst(a: string | undefined): boolean {
  return !!a && CONSTS[a] !== undefined;
}

// ℓ : longueur (« ℓ = x − 0,6 ») ; Ĉ, Ĝ… : angles (moteur v21, « Ĉ = 37 »)
const LETTER = 'A-Za-zÀ-ÖØ-öø-ÿΑ-Ωα-ωℓĈĜĤĴŜŴŶ';
const SUB = '₀-ₜ'; // indices : uₙ, uₙ₊₁, u₀
const SUP = '⁰¹²³⁴-⁹⁺⁻ⁿ'; // exposants : 2ⁿ, 2ⁿ⁺¹, 2¹⁰
const RE_LETTER = new RegExp('[' + LETTER + ']');
// x̄, ȳ (moyennes), B̂ (angle, moteur v21) : la barre ou le chapeau combinant fait partie du nom
// eslint-disable-next-line no-misleading-character-class
const RE_WORD = new RegExp('^[' + LETTER + '][' + LETTER + SUB + '\\u0302\\u0304]*');
const RE_SUP = new RegExp('^[' + SUP + ']+');
const RE_POW = new RegExp('^\\d+[' + SUP + ']+$');

/** Un bout posé sur la ligne : ses atomes et son texte affiché. */
export interface Chip {
  atoms: string[];
  text?: string;
  kind?: string;
}

/** Modulo d'une congruence (moteur v3) : l'atome « [7] », écrit à la fin de la ligne. */
export function isMod(a: string | undefined): boolean {
  return /^\[\d+\]$/.test(a || '');
}
export function modValue(a: string): number {
  return Number(a.slice(1, -1));
}

export function isNum(a: string | undefined): boolean {
  return /^\d+(?:,\d+)?$/.test(a || '');
}
export function isVar(a: string | undefined): boolean {
  return (
    RE_LETTER.test((a || '')[0] || '') && a !== 'ou' && a !== PARMI && !isFunc(a) && !isConst(a)
  );
}
export function isPow(a: string | undefined): boolean {
  return RE_POW.test(a || '');
}
/**
 * Exposant écrit en chiffres après une lettre ou une parenthèse (moteur v41) : « x³ », « (2x)³ »,
 * « a¹⁰ » forment UN bout « ³ », « ¹⁰ », lu x^3 par le parseur. « ² » seul reste le carré.
 */
const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
export function isSupPow(a: string | undefined): boolean {
  return !!a && a !== '²' && [...a].every((c) => SUP_DIGITS.includes(c));
}
export function supPowValue(a: string): number {
  return Number([...a].map((c) => String(SUP_DIGITS.indexOf(c))).join(''));
}
export function isOperand(a: string | undefined): boolean {
  return isNum(a) || isVar(a) || isPow(a) || isConst(a);
}

/**
 * Notation fonctionnelle (moteur v2) : « P(A) », « P(A∩B) », « P_A(B) »,
 * « P(Ā) », « f(2) », « f′(x) », « E(X) » forment UNE grandeur (sinon « P(A) »
 * se lirait P × A). Base d'une ou deux lettres (+ une ou deux primes, + indice « _A ») ;
 * le contenu des parenthèses ne contient AUCUNE opération : « x(x + 3) » reste
 * un produit. Renvoie l'atome normalisé (sans espaces) et sa longueur lue.
 */
// Les accents combinants (barre de Ā) sont voulus dans la classe : P(Ā), P_S̄(M).
const RE_COMPOUND = new RegExp(
  // eslint-disable-next-line no-misleading-character-class
  "^(card|[A-Za-zα-ωΑ-Ω]{1,2})([′'’]{0,2}|″)(_[A-Za-zĀ-ſ][\\u0300-\\u036F]?[₀-₉]*)?\\(\\s*(−|-)?([A-Za-zÀ-ÖØ-öø-ÿĀ-ſα-ωΑ-Ω\\u0300-\\u036F0-9₀-₉∩∪×|,;\\s]+?)\\s*\\)",
);
export function compoundAt(s: string): { atom: string; len: number } | null {
  const m = RE_COMPOUND.exec(s);
  if (!m) return null;
  const base = m[1]!;
  if (isFunc(base) || base === 'ou') return null;
  const inner = m[5]!.replace(/\s+/g, '');
  if (!inner) return null;
  // un nombre seul derrière une lettre : « f(2) » ; mais « a(2) »… est rarissime
  // et toujours écrit « 2a » : on accepte.
  // « f′′(x) », « f″(x) », « f''(x) » : la dérivée seconde (lot 6.18), écrite « f′′(x) »
  const prime = '′'.repeat(m[2] === '″' ? 2 : (m[2] ?? '').length);
  const sub = m[3] ?? '';
  const minus = m[4] ? '−' : '';
  return { atom: `${base}${prime}${sub}(${minus}${inner})`, len: m[0].length };
}

export function formatNumber(v: number): string {
  if (!isFinite(v)) return '?';
  // un très petit nombre (« 0,00000035 », 4 × 10⁻⁹) garde ses chiffres significatifs :
  // l'arrondi à 6 décimales l'écrivait 0. En dessous de 10⁻⁹ : le bruit d'un calcul en
  // flottants (sin π ≈ 1,2 × 10⁻¹⁶), écrit 0 comme avant.
  const av = Math.abs(v);
  if (av >= 1e-9 && av < 1e-4) {
    const digits = Math.min(20, Math.ceil(-Math.log10(av)) + 9);
    const small = Number(av.toPrecision(10)).toFixed(digits).replace(/0+$/, '').replace('.', ',');
    return (v < 0 ? '−' : '') + small;
  }
  const r = Math.round(v * 1e6) / 1e6;
  const neg = r < 0;
  const a = Math.abs(r);
  let s = Number.isInteger(a) ? String(a) : String(Number(a.toFixed(6)));
  s = s.replace('.', ',');
  return (neg ? '−' : '') + s;
}

export function canonNumber(s: string): string {
  // un nombre ÉCRIT garde tous ses chiffres : « 0,00000035 » restait « 0 » (arrondi
  // d'affichage à 6 décimales), « 0,1234567 » devenait « 0,123457 »
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(String(s));
  if (m) {
    const int = m[1]!.replace(/^0+(?=\d)/, '');
    const frac = (m[2] ?? '').replace(/0+$/, '');
    return frac ? `${int},${frac}` : int;
  }
  const n = Number(String(s).replace(',', '.'));
  if (!isFinite(n)) return String(s);
  return formatNumber(n);
}

/* --------- texte -> atomes --------- */
export function lex(str: string): string[] {
  const out: string[] = [];
  let i = 0;
  const s = String(str);
  while (i < s.length) {
    const c = s[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/\d/.test(c)) {
      const m = /^\d+(?:[.,]\d+)?/.exec(s.slice(i))!;
      i += m[0].length;
      const sup = RE_SUP.exec(s.slice(i));
      if (sup && sup[0] !== '²' && /^\d+$/.test(m[0])) {
        // une puissance tapée d'un bloc : 2ⁿ, 2ⁿ⁺¹, 2⁰, 2¹⁰
        out.push(m[0] + sup[0]);
        i += sup[0].length;
      } else {
        out.push(canonNumber(m[0]));
      }
      continue;
    }
    if (RE_LETTER.test(c)) {
      const rest = s.slice(i);
      // « e^x » / « e^(2x) » : l'exponentielle (moteur v2)
      if (c === 'e' && s[i + 1] === '^' && !RE_LETTER.test(s[i - 1] ?? '')) {
        out.push('exp');
        i += 2;
        continue;
      }
      const comp = compoundAt(rest);
      if (comp) {
        out.push(comp.atom);
        i += comp.len;
        continue;
      }
      const w = RE_WORD.exec(rest)![0];
      // « cosx », « lnx » : la fonction, puis son argument
      const fn = Object.keys(FUNCS).find(
        (f) => w.length === f.length + 1 && w.startsWith(f) && /[xtθ]$/.test(w),
      );
      if (fn && !RE_LETTER.test(s[i - 1] ?? '')) {
        out.push(fn);
        i += fn.length;
        continue;
      }
      // π est une constante, jamais une partie de nom (moteur v24) : « 2kπ » = 2 × k × π
      const pi = w.indexOf('π');
      if (pi >= 0 && w.length > 1) {
        const head = pi === 0 ? 'π' : w.slice(0, pi);
        out.push(head);
        i += head.length;
        continue;
      }
      out.push(w);
      i += w.length;
      continue;
    }
    if (c === '-' || c === '−') {
      out.push('−');
      i++;
      continue;
    }
    if (c === '*' || c === '×') {
      out.push('×');
      i++;
      continue;
    }
    if (c === '/' || c === '÷') {
      out.push('÷');
      i++;
      continue;
    }
    if (c === '^' && s[i + 1] === '2' && !/[\d.,]/.test(s[i + 2] ?? '')) {
      out.push('²');
      i += 2;
      continue;
    }
    if (c === '^') {
      // puissance générale (moteur v2) : 1,05^n, q^(n − 1), 2^10
      out.push('^');
      i++;
      continue;
    }
    if (c === '[') {
      // modulo d'une congruence (moteur v3) : « [7] » forme un seul atome
      const m = /^\[\s*(\d+)\s*\]/.exec(s.slice(i));
      if (m) {
        out.push('[' + m[1] + ']');
        i += m[0].length;
        continue;
      }
    }
    if (c === '≡') {
      out.push('≡');
      i++;
      continue;
    }
    // module (moteur v11) : « |z| »
    if (c === '|') {
      out.push('|');
      i++;
      continue;
    }
    // valeur arrondie (moteur v10) : « AC ≈ 8,4 » (aussi « ≃ » et « ~= »)
    if (c === '≈' || c === '≃' || (c === '~' && s[i + 1] === '=')) {
      out.push('≈');
      i += c === '~' ? 2 : 1;
      continue;
    }
    if (c === '>' || c === '<' || c === '≥' || c === '≤') {
      // comparaison (moteur v5) : « >= » s'écrit aussi « ≥ »
      if ((c === '>' || c === '<') && s[i + 1] === '=') {
        out.push(c === '>' ? '≥' : '≤');
        i += 2;
      } else {
        out.push(c);
        i++;
      }
      continue;
    }
    if (c === '!') {
      // factorielle (moteur v6) : 5!, n!, (n − k)!
      out.push('!');
      i++;
      continue;
    }
    if (c === '°') {
      // degré (moteur v2) : cos(60°)
      out.push('°');
      i++;
      continue;
    }
    if (SUP_DIGITS.includes(c)) {
      // moteur v41 : « x³ », « (2x)³ », « a¹⁰ », « 1,5³ » — l'exposant après une lettre, une
      // parenthèse ou un décimal forme un bout, lu comme une puissance (« ² » seul reste le
      // carré ; « x²⁵ » est un bout « ²⁵ »)
      const p = out[out.length - 1];
      const run = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]+/.exec(s.slice(i))![0];
      if (p !== undefined && (p === ')' || isVar(p) || isConst(p) || isNum(p)) && run !== '²') {
        out.push(run);
        i += run.length;
        continue;
      }
    }
    if ('+=()²√%'.indexOf(c) >= 0) {
      out.push(c);
      i++;
      continue;
    }
    if ('⁰¹³⁴⁵⁶⁷⁸⁹⁻'.indexOf(c) >= 0) {
      // exposant ailleurs (après un opérateur, en début de ligne) : gardé, pour que la ligne
      // soit déclarée illisible au lieu d'être lue en silence. De même « ⁻ » après une
      // parenthèse ou une lettre : « (2 ÷ 3)⁻² » se lisait « (2 ÷ 3)² » (le signe ignoré) ;
      // on l'écrit « (2 ÷ 3)^(−2) ». « 10⁻² » (après un entier) reste une puissance.
      out.push(c);
      i++;
      continue;
    }
    i++; // caractère ignoré (unités, ponctuation)
  }
  return out;
}

/* --------- multiplication implicite + carte vers les bouts --------- */
function endsOperand(a: string): boolean {
  return (
    isOperand(a) || a === ')' || a === '²' || isSupPow(a) || a === '%' || a === '°' || a === '!'
  );
}
function startsOperandImplicit(a: string): boolean {
  return isVar(a) || isConst(a) || isFunc(a) || a === '(' || a === '√';
}

/** chips → { atoms, src : index du bout pour chaque atome } */
export function normalizeChips(chips: Chip[]): { atoms: string[]; src: number[] } {
  const atoms: string[] = [];
  const src: number[] = [];
  // module (moteur v11) : une barre « | » ouvre ou ferme, selon les barres déjà posées
  let barOpen = false;
  let prevClosingBar = false;
  chips.forEach((chip, ci) => {
    chip.atoms.forEach((a) => {
      const prev = atoms[atoms.length - 1];
      const prevEnds = prev !== undefined && (endsOperand(prev) || prevClosingBar);
      const opensBar = a === '|' && !barOpen;
      if (prevEnds && (startsOperandImplicit(a) || opensBar)) {
        atoms.push('×');
        src.push(ci);
      }
      prevClosingBar = a === '|' && barOpen;
      if (a === '|') barOpen = !barOpen;
      atoms.push(a);
      src.push(ci);
    });
  });
  return { atoms, src };
}
export function normalize(atoms: string[]): string[] {
  return normalizeChips([{ atoms }]).atoms;
}

/* --------- découpe au niveau 0 --------- */
export function splitTop(atoms: string[], seps: string[]): { parts: string[][]; ops: string[] } {
  const parts: string[][] = [[]];
  const ops: string[] = [];
  let depth = 0;
  atoms.forEach((a) => {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && seps.indexOf(a) >= 0) {
      ops.push(a);
      parts.push([]);
    } else {
      parts[parts.length - 1]!.push(a);
    }
  });
  return { parts, ops };
}

function permutations<T>(arr: T[]): T[][] {
  if (arr.length <= 1) return [arr.slice()];
  if (arr.length > 4) return [arr.slice()];
  const res: T[][] = [];
  arr.forEach((x, i) => {
    const rest = arr.slice(0, i).concat(arr.slice(i + 1));
    permutations(rest).forEach((p) => res.push([x].concat(p)));
  });
  return res;
}

function joinParts(parts: string[][], op: string): string[] {
  let out: string[] = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(op);
    out = out.concat(p);
  });
  return out;
}

function sideVariants(side: string[]): string[][] {
  const sum = splitTop(side, ['+', '−']);
  if (
    sum.parts.length > 1 &&
    sum.ops.every((o) => o === '+') &&
    sum.parts.every((p) => p.length && p[0] !== '−')
  ) {
    return permutations(sum.parts).map((p) => joinParts(p, '+'));
  }
  const prod = splitTop(side, ['×', '÷']);
  if (prod.parts.length > 1 && prod.ops.every((o) => o === '×')) {
    return permutations(prod.parts).map((p) => joinParts(p, '×'));
  }
  return [side];
}

function equationVariants(eq: string[]): string[][] {
  const sides = splitTop(eq, ['=']).parts;
  if (sides.length === 1) return sideVariants(sides[0]!);
  if (sides.length !== 2) return [eq];
  const res: string[][] = [];
  sideVariants(sides[0]!).forEach((L) => {
    sideVariants(sides[1]!).forEach((R) => {
      res.push(L.concat(['=']).concat(R));
      res.push(R.concat(['=']).concat(L));
    });
  });
  return res;
}

export function formVariants(atoms: string[]): string[][] {
  const dis = splitTop(atoms, ['ou']).parts;
  const perDis = dis.map(equationVariants);
  const orders = dis.length <= 3 ? permutations(dis.map((_, i) => i)) : [dis.map((_, i) => i)];
  const res: string[][] = [];
  orders.forEach((order) => {
    let combos: string[][][] = [[]];
    order.forEach((di) => {
      const next: string[][][] = [];
      combos.forEach((c) => {
        perDis[di]!.forEach((v) => next.push(c.concat([v])));
      });
      combos = next.slice(0, 200);
    });
    combos.forEach((c) => res.push(joinParts(c, 'ou')));
  });
  // l'écriture d'origine en premier, sans doublons
  const seen: Record<string, 1> = {};
  const out: string[][] = [];
  [atoms].concat(res).forEach((v) => {
    const k = v.join(SEP);
    if (!seen[k]) {
      seen[k] = 1;
      out.push(v);
    }
  });
  return out;
}

export function key(atoms: string[]): string {
  return atoms.join(SEP) + SEP;
}

export type SyntaxResult =
  | { ok: true; complete: boolean; hasEq: boolean }
  | { ok: false; index: number; msg: string };

/* --------- syntaxe (ligne partielle ou complète) --------- */
export function checkSyntax(atoms: string[], opts?: { chains?: boolean }): SyntaxResult {
  // comparaisons en chaîne (moteur v23, feuilles du noyau) : « −1 < x ≤ 2 »
  const chains = !!opts?.chains;
  let expect = true;
  let depth = 0;
  let seenEq = false;
  // congruence (moteur v3) : « ≡ » dans l'égalité en cours, modulo « [7] » posé
  let cong = false;
  let modded = false;
  // comparaison (moteur v5) : « Δ > 0 », une seule relation dans la ligne
  let cmp = false;
  let lastCmp = '';
  // module (moteur v11) : barres « | » ouvertes
  let bars = 0;
  const err = (index: number, msg: string): SyntaxResult => ({ ok: false, index, msg });
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i]!;
    if (expect) {
      if (isOperand(a)) {
        expect = false;
        continue;
      }
      if (a === '(') {
        depth++;
        continue;
      }
      if (a === '√' || isFunc(a)) continue;
      if (a === '|') {
        bars++;
        continue;
      }
      if (a === '−' || a === '+') {
        const p = atoms[i - 1];
        if (p === '−' || p === '+')
          return err(i, 'Deux signes à la suite : il manque un nombre entre les deux.');
        continue;
      }
      if (a === '=' || a === '≡' || isCmp(a))
        return err(
          i,
          i === 0
            ? 'Une ligne ne commence pas par « ' + a + ' ».'
            : 'Il manque un nombre ou une lettre avant « ' + a + ' ».',
        );
      if (isMod(a))
        return err(
          i,
          '« ' + a + ' » se place à la fin d’une congruence, après un nombre ou une lettre.',
        );
      if (BIN[a]) return err(i, 'Deux opérations à la suite : il manque un nombre entre les deux.');
      if (a === ')') return err(i, 'Parenthèse fermée trop tôt : il manque quelque chose dedans.');
      if (a === '²' || isSupPow(a) || a === '%' || a === '°' || a === '!')
        return err(
          i,
          '« ' + a + ' » se place juste après un nombre, une lettre ou une parenthèse.',
        );
      if (a === 'ou') return err(i, '« ou » relie deux égalités complètes.');
      return err(i, 'Ce bout ne peut pas venir ici.');
    }
    if (modded && a !== 'ou')
      return err(
        i,
        'Le modulo « ' + atoms[i - 1] + ' » termine la congruence : rien ne vient après.',
      );
    if (BIN[a]) {
      expect = true;
      continue;
    }
    if (a === '|' && bars > 0) {
      bars--;
      continue;
    }
    if (isCmp(a)) {
      if (depth > 0) return err(i, 'Ferme la parenthèse avant « ' + a + ' ».');
      if (chains && cmp && a !== '≈') {
        // une chaîne garde son sens : « −1 < x ≤ 2 », jamais « 1 < x > 2 »
        if (LOWER_CMP[a] !== LOWER_CMP[lastCmp])
          return err(i, 'Une chaîne de comparaisons va dans un seul sens : « a < x < b ».');
        lastCmp = a;
        expect = true;
        continue;
      }
      if (seenEq || cmp)
        return err(
          i,
          chains
            ? 'Une comparaison relie deux membres : pas de « = » en plus.'
            : 'Une comparaison relie deux membres : une seule par ligne, sans « = ».',
        );
      lastCmp = a;
      cmp = true;
      seenEq = true;
      expect = true;
      continue;
    }
    if (a === '=' || a === '≡') {
      if (depth > 0) return err(i, 'Ferme la parenthèse avant « ' + a + ' ».');
      if (cmp) return err(i, 'Une comparaison relie deux membres : pas de « = » en plus.');
      seenEq = true;
      if (a === '≡') cong = true;
      expect = true;
      continue;
    }
    if (isMod(a)) {
      if (depth > 0) return err(i, 'Ferme la parenthèse avant le modulo « ' + a + ' ».');
      if (!cong)
        return err(i, 'Le modulo « ' + a + ' » suit une congruence : écris d’abord « ≡ ».');
      modded = true;
      continue;
    }
    if (a === ')') {
      if (depth === 0) return err(i, 'Cette parenthèse fermante n’a pas de parenthèse ouvrante.');
      depth--;
      continue;
    }
    if (a === '²' || isSupPow(a) || a === '%' || a === '°' || a === '!') continue;
    if (a === 'ou') {
      if (depth > 0) return err(i, 'Ferme la parenthèse avant « ou ».');
      if (!seenEq) return err(i, 'Écris d’abord une égalité complète avant « ou ».');
      if (cong && !modded)
        return err(i, 'Termine la congruence par son modulo (« [7] ») avant « ou ».');
      seenEq = false;
      cong = false;
      modded = false;
      cmp = false;
      expect = true;
      continue;
    }
    if (isNum(a) || isPow(a))
      return err(i, 'Deux nombres collés : ajoute une opération entre les deux.');
    return err(i, 'Ce bout ne peut pas venir ici.');
  }
  return {
    ok: true,
    complete: !expect && depth === 0 && bars === 0 && (!cong || modded),
    hasEq: seenEq,
  };
}
